import { Beer, Utensils } from 'lucide-react';
import React from 'react';
import { STATUS_CONFIG } from '../constants';
import { Category, RecommendationStatus } from '../types';

interface MapPinProps {
  status: RecommendationStatus;
  category: Category;
  count?: number;
  onClick: () => void;
  isSelected: boolean;
  name?: string;
  /** Photo of the friend whose shout gives this venue its primary status. */
  avatarUrl?: string;
}

/*
 * "Stamp" pin geometry (SVG user units == CSS px).
 * A 36x36 rounded square with a 13px-wide tail that comes to a point at
 * (TIP_X, TIP_Y). That point is the venue's lat/lng, so the whole pin is
 * translated by (-TIP_X, -TIP_Y) and scaled about the tip.
 */
const BOX = 36;
const RADIUS = 10;
const PAD = 4; // room for the white outline + drop shadow
const TIP_X = PAD + BOX / 2; // 22
const TIP_Y = PAD + BOX + 9; // 49
const SVG_W = PAD * 2 + BOX; // 44
const SVG_H = TIP_Y + 3; // 52

const STAMP_PATH = [
  `M ${PAD + RADIUS} ${PAD}`,
  `H ${PAD + BOX - RADIUS}`,
  `A ${RADIUS} ${RADIUS} 0 0 1 ${PAD + BOX} ${PAD + RADIUS}`,
  `V ${PAD + BOX - RADIUS}`,
  `A ${RADIUS} ${RADIUS} 0 0 1 ${PAD + BOX - RADIUS} ${PAD + BOX}`,
  `H ${TIP_X + 6.5}`,
  `L ${TIP_X} ${TIP_Y}`,
  `L ${TIP_X - 6.5} ${PAD + BOX}`,
  `H ${PAD + RADIUS}`,
  `A ${RADIUS} ${RADIUS} 0 0 1 ${PAD} ${PAD + BOX - RADIUS}`,
  `V ${PAD + RADIUS}`,
  `A ${RADIUS} ${RADIUS} 0 0 1 ${PAD + RADIUS} ${PAD}`,
  'Z',
].join(' ');

export const MapPin: React.FC<MapPinProps> = ({ status, category, count = 1, onClick, isSelected, name, avatarUrl }) => {
  const config = STATUS_CONFIG[status];
  const statusColor = `var(${config.cssVar})`;

  // "Want to visit" is a hollow stamp: white fill, status-coloured outline + glyph.
  const isHollow = status === RecommendationStatus.WANT_TO_VISIT;

  const ref = React.useRef<HTMLButtonElement>(null);

  /*
   * Each pin lives in its own OverlayView container (siblings in the map pane
   * with no z-index), so a z-index on the button alone cannot lift it above a
   * neighbouring pin. Raise the container itself when selected.
   */
  React.useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    parent.style.zIndex = isSelected ? '30' : '';
    return () => {
      parent.style.zIndex = '';
    };
  }, [isSelected]);

  /*
   * A broken avatar URL hides the image, but the flag must reset when the URL
   * changes — React reuses this component across venues, so an imperative
   * `style.display = 'none'` would stick and blank out the next friend's photo.
   */
  const [avatarBroken, setAvatarBroken] = React.useState(false);
  React.useEffect(() => {
    setAvatarBroken(false);
  }, [avatarUrl]);

  const glyphColor = isHollow ? statusColor : '#FFFFFF';

  const renderGlyph = () => {
    if (count > 1) {
      return (
        <span
          className="font-bold text-[15px] leading-none tabular-nums"
          style={{ color: glyphColor }}
        >
          {count}
        </span>
      );
    }
    const Glyph = category === Category.FOOD ? Utensils : Beer;
    return <Glyph size={19} strokeWidth={2.5} style={{ color: glyphColor }} />;
  };

  return (
    <button
      ref={ref}
      type="button"
      className="absolute left-0 top-0 cursor-pointer transition-transform duration-200 ease-out motion-reduce:transition-none"
      style={{
        width: SVG_W,
        height: SVG_H,
        transformOrigin: `${TIP_X}px ${TIP_Y}px`,
        transform: `translate(${-TIP_X}px, ${-TIP_Y}px) scale(${isSelected ? 1.15 : 1})`,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={name ? `Map pin for ${name}` : `Map pin for ${category} - ${status}`}
    >
      {/* Stamp body: one path so the white outline wraps the square AND the tail. */}
      <svg
        width={SVG_W}
        height={SVG_H}
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="absolute inset-0 pointer-events-none"
        style={{
          filter: isSelected
            ? 'drop-shadow(0 5px 6px rgba(55, 65, 81, 0.38))'
            : 'drop-shadow(0 2px 3px rgba(55, 65, 81, 0.28))',
        }}
        aria-hidden="true"
      >
        <path
          d={STAMP_PATH}
          fill={isHollow ? '#FFFFFF' : statusColor}
          stroke={isHollow ? statusColor : '#FFFFFF'}
          strokeWidth={isHollow ? 3 : 2}
          strokeLinejoin="round"
        />
      </svg>

      {/* Category glyph (or shout count) centred on the square. */}
      <span
        className="absolute flex items-center justify-center pointer-events-none"
        style={{ left: PAD, top: PAD, width: BOX, height: BOX }}
      >
        {renderGlyph()}
      </span>

      {/* Friend avatar, overlapping the top-right corner. */}
      {avatarUrl && !avatarBroken && (
        <img
          src={avatarUrl}
          alt=""
          aria-hidden="true"
          className="absolute rounded-full object-cover bg-brand-cream-dark pointer-events-none"
          style={{
            width: 18,
            height: 18,
            left: PAD + BOX - 9,
            top: 0,
            border: '2px solid #FFFFFF',
            boxShadow: '0 1px 2px rgba(55, 65, 81, 0.35)',
          }}
          onError={() => setAvatarBroken(true)}
        />
      )}
    </button>
  );
};
